export const PLANNING_COMMENT_ORIGIN_ATTR = 'data-planning-comment-origin-id';

const STORAGE_PREFIX = 'planning-comment-origin:';
const originByThreadId = new Map<string, string>();

const storageKeyFor = (threadId: string) => `${STORAGE_PREFIX}${threadId}`;

const readStoredOrigin = (threadId: string): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage.getItem(storageKeyFor(threadId));
  } catch {
    return null;
  }
};

const writeStoredOrigin = (threadId: string, originId: string) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKeyFor(threadId), originId);
  } catch {
    /* ignore storage failures */
  }
};

export const getPlanningCommentOriginIdFromElement = (
  element: HTMLElement | null | undefined
): string | null =>
  element
    ?.closest<HTMLElement>(`[${PLANNING_COMMENT_ORIGIN_ATTR}]`)
    ?.getAttribute(PLANNING_COMMENT_ORIGIN_ATTR) ?? null;

export const rememberPlanningCommentOrigin = (
  threadId: string | null | undefined,
  origin: HTMLElement | string | null | undefined
) => {
  if (!threadId || !origin) return;

  const originId =
    typeof origin === 'string' ? origin : getPlanningCommentOriginIdFromElement(origin);
  if (!originId) return;

  originByThreadId.set(threadId, originId);
  writeStoredOrigin(threadId, originId);
};

export const getRememberedPlanningCommentOrigin = (
  threadId: string | null | undefined
): string | null => {
  if (!threadId) return null;

  const cached = originByThreadId.get(threadId);
  if (cached) return cached;

  const stored = readStoredOrigin(threadId);
  if (stored) {
    originByThreadId.set(threadId, stored);
  }
  return stored;
};

export const filterElementsToPlanningCommentOrigin = <T extends HTMLElement>(
  threadId: string,
  elements: T[],
  originId?: string | null
): T[] => {
  const resolvedOriginId = originId ?? getRememberedPlanningCommentOrigin(threadId);
  if (!resolvedOriginId) return elements;

  const matchingElements = elements.filter(
    (element) => getPlanningCommentOriginIdFromElement(element) === resolvedOriginId
  );
  return matchingElements.length > 0 ? matchingElements : elements;
};

export const clearRememberedPlanningCommentOriginsForTest = () => {
  originByThreadId.clear();
};
